import * as React from "react";
import { useContext, useRef, useState } from "react";
import { useTheme } from "@mui/material/styles";
import { CajasContext } from "../../../context/CajasContext/CajasContext";
import Typography from "@mui/material/Typography";
import MoveUpIcon from "@mui/icons-material/MoveUp";
import WhereToVoteIcon from "@mui/icons-material/WhereToVote";
import OutputIcon from "@mui/icons-material/Output";
import InputIcon from "@mui/icons-material/Input";
import ShareLocationIcon from "@mui/icons-material/ShareLocation";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import PrintIcon from "@mui/icons-material/Print";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PersonIcon from "@mui/icons-material/Person";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import { Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, IconButton, Paper } from "@mui/material";
import { comprobantesPrintData } from "../helpers/ComprobantesData";

// ─── Config por tipo ──────────────────────────────────────────────────────────

const MOVIMIENTO_CONFIG = {
  "Alta de caja":                   { icon: <Inventory2Icon />,         color: "#059669", bg: "#ecfdf5" },
  "Planillado":                     { icon: <RequestQuoteIcon />,       color: "#475569", bg: "#f8fafc" },
  "Ubicación inicial":              { icon: <WhereToVoteIcon />,        color: "#2563eb", bg: "#eff6ff" },
  "Mudanza":                        { icon: <MoveUpIcon />,             color: "#b45309", bg: "#fffbeb" },
  "Retiro Parcial":                 { icon: <OutputIcon />,             color: "#dc2626", bg: "#fef2f2" },
  "Retiro Total":                   { icon: <OutputIcon />,             color: "#dc2626", bg: "#fef2f2" },
  "Retiro de caja":                 { icon: <OutputIcon />,             color: "#dc2626", bg: "#fef2f2" },
  "Devolución Total":               { icon: <InputIcon />,              color: "#0891b2", bg: "#ecfeff" },
  "Devolución Parcial":             { icon: <InputIcon />,              color: "#0891b2", bg: "#ecfeff" },
  "Creación de ubicación":          { icon: <ShareLocationIcon />,      color: "#7c3aed", bg: "#f5f3ff" },
  "Alojamiento de caja generica":   { icon: <GpsFixedIcon />,           color: "#475569", bg: "#f8fafc" },
  "Ubicación tempral":              { icon: <SaveAltIcon />,            color: "#b45309", bg: "#fffbeb" },
  "Quita Total":                    { icon: <DeleteForeverIcon />,      color: "#b91c1c", bg: "#fef2f2" },
  "Alta":                           { icon: <AddCircleOutlineIcon />,   color: "#059669", bg: "#ecfdf5" },
  "Salida por remito de contenido": { icon: <LocalShippingIcon />,      color: "#dc2626", bg: "#fef2f2" },
  "Salida por remito liberando":    { icon: <LockOpenIcon />,           color: "#b45309", bg: "#fffbeb" },
  "Migración":                      { icon: <SyncAltIcon />,            color: "#4338ca", bg: "#eef2ff" },
  "Migracion":                      { icon: <SyncAltIcon />,            color: "#4338ca", bg: "#eef2ff" },
};

const getConfig = (tipo) =>
  MOVIMIENTO_CONFIG[tipo] || { icon: <GpsFixedIcon />, color: "#78909c", bg: "#fafafa" };

// ─── Helpers ──────────────────────────────────────────────────────────────────

const capitalizeFirst = (str) => str.charAt(0).toUpperCase() + str.slice(1);

const parseExtraData = (extra_data) => {
  try {
    const corrected = extra_data.replace(/'/g, '"');
    return JSON.parse("[" + corrected.replace(/;/g, ",") + "]");
  } catch {
    return [];
  }
};

const splitFecha = (fecha) => {
  if (!fecha || typeof fecha === "object") return { date: "", time: "" };
  const parts = fecha.trim().split(" ");
  return { date: parts[0] || "", time: parts[1]?.slice(0, 5) || "" };
};

const ExtraDataRows = ({ extra_data, useLabel = false }) => {
  if (!extra_data?.length) return null;
  const objects = parseExtraData(extra_data);
  return objects.map((obj, i) => (
    <Box key={i}>
      {Object.entries(obj)
        .filter(([key]) => key !== "field")
        .map(([key, val]) => (
          <Box key={key} sx={{ display: "flex", gap: 0.5, mt: 0.4 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
              {capitalizeFirst(key.replace(/_/g, " "))}:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {useLabel ? val?.label || val?.value : val?.value}
            </Typography>
          </Box>
        ))}
    </Box>
  ));
};

// ─── Date badge ───────────────────────────────────────────────────────────────

const DateBadge = ({ fecha, align = "right" }) => {
  const theme = useTheme();
  const { date, time } = splitFecha(fecha);
  if (!date) return <Box sx={{ flex: 1 }} />;

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: align === "right" ? "flex-end" : "flex-start",
        px: 1.5,
        gap: 0.4,
      }}
    >
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          bgcolor: theme.palette.primary.main,
          color: "#fff",
          borderRadius: "6px",
          px: 1,
          py: 0.3,
        }}
      >
        <CalendarTodayIcon sx={{ fontSize: 14 }} />
        <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, letterSpacing: 0.3 }}>
          {date}
        </Typography>
      </Box>
      {time && (
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.4 }}>
          <AccessTimeIcon sx={{ fontSize: 13, color: "text.disabled" }} />
          <Typography sx={{ color: "text.disabled", fontSize: "0.78rem" }}>
            {time}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// ─── Dot ──────────────────────────────────────────────────────────────────────

const Dot = ({ config }) => (
  <Box
    sx={{
      width: 46,
      height: 46,
      borderRadius: "50%",
      bgcolor: config.color,
      boxShadow: `0 0 0 4px ${config.color}28, 0 2px 8px ${config.color}50`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      zIndex: 1,
      position: "relative",
    }}
  >
    {React.cloneElement(config.icon, { sx: { fontSize: 22, color: "#fff" } })}
  </Box>
);

// ─── Card ─────────────────────────────────────────────────────────────────────

const Card = ({ config, tipo_movimiento, isLeft, fields, extraData, useLabel, printBtn }) => (
  <Box sx={{ flex: 2, px: 1.5, py: 0.75 }}>
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        borderLeft: !isLeft ? `3px solid ${config.color}` : "none",
        borderRight: isLeft ? `3px solid ${config.color}` : "none",
        bgcolor: config.bg,
        borderRadius: isLeft ? "8px 0 0 8px" : "0 8px 8px 0",
        transition: "box-shadow 0.2s",
        "&:hover": { boxShadow: `0 2px 12px ${config.color}30` },
      }}
    >
      <Chip
        label={tipo_movimiento}
        size="small"
        sx={{
          bgcolor: config.color,
          color: "#fff",
          fontWeight: 700,
          fontSize: "0.8rem",
          height: 24,
          mb: 1,
        }}
      />

      {fields.map(({ icon: Icon, label, value }) =>
        value ? (
          <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
            {Icon && <Icon sx={{ fontSize: 14, color: "text.disabled" }} />}
            {label && (
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                {label}:
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              {value}
            </Typography>
          </Box>
        ) : null
      )}

      <ExtraDataRows extra_data={extraData} useLabel={useLabel} />
      {printBtn}
    </Paper>
  </Box>
);

// ─── Fila del timeline ────────────────────────────────────────────────────────

const TimelineRow = ({ tipo_movimiento, fecha, fields = [], extraData, useLabel, printBtn }) => {
  const config = getConfig(tipo_movimiento);

  return (
    <Box sx={{ display: "flex", width: "100%", minHeight: 90 }}>
      {/* Movimiento */}
      <Box sx={{ flex: 2, display: "flex", alignItems: "center" }}>
        <Card config={config} tipo_movimiento={tipo_movimiento} isLeft fields={fields} extraData={extraData} useLabel={useLabel} printBtn={printBtn} />
      </Box>

      {/* Dot + línea vertical en la misma columna */}
      <Box sx={{ width: 46, flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center" }}>
        <Box sx={{ flex: 1, width: 2, bgcolor: "#dde2ec", minHeight: 16 }} />
        <Dot config={config} />
        <Box sx={{ flex: 1, width: 2, bgcolor: "#dde2ec", minHeight: 16 }} />
      </Box>

      {/* Fecha */}
      <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
        <DateBadge fecha={fecha} align="left" />
      </Box>
    </Box>
  );
};

// ─── Wrapper ─────────────────────────────────────────────────────────────────

const TimelineWrapper = ({ children }) => (
  <Box sx={{ maxWidth: 720, mx: "auto", width: "100%", px: 1, py: 1 }}>
    {children}
  </Box>
);

// ─── Componente principal ─────────────────────────────────────────────────────

export const ComprobanteModal = ({ open, html, onClose }) => {
  const iframeRef = useRef(null);
  const theme = useTheme();

  const handlePrint = () => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const doc = iframe.contentDocument;
    if (doc) {
      const style = doc.createElement("style");
      style.textContent = "@media print { @page { margin: 0mm; size: auto; } body { margin: 1cm; } }";
      doc.head.appendChild(style);
    }
    iframe.contentWindow?.print();
  };

  const handleDownload = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;
    const style = "<style>@media print { @page { margin: 0mm; size: auto; } body { margin: 1cm; } }</style>";
    printWindow.document.write(html.replace("</head>", style + "</head>"));
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
    };
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: "10px", overflow: "hidden", boxShadow: "0 8px 32px rgba(60,75,100,0.18)", height: "85vh" } }}>
      <DialogTitle sx={{ py: 1.5, px: 2.5, m: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="subtitle1" sx={{ color: "#fff", fontWeight: 700, letterSpacing: 0.4, fontSize: "0.95rem" }}>
            Comprobante
          </Typography>
          <IconButton onClick={onClose} size="small" sx={{ color: "rgba(255,255,255,0.6)", "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.12)" } }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column" }}>
        <iframe
          ref={iframeRef}
          srcDoc={html}
          style={{ flex: 1, width: "100%", border: "none" }}
          title="Comprobante"
        />
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 2.5, py: 1.5, gap: 1 }}>
        <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={handleDownload}>
          Descargar
        </Button>
        <Button variant="contained" size="small" startIcon={<PrintIcon />} onClick={handlePrint}>
          Imprimir
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const MovimientosTimelineMUI = (data) => {
  const { tipoHistorico, valorContenido } = useContext(CajasContext);
  const [comprobanteHtml, setComprobanteHtml] = useState(null);
  const [comprobanteLoading, setComprobanteLoading] = useState(false);

  const handlePrint = async (id_comprobante) => {
    if (!id_comprobante) return;
    setComprobanteLoading(id_comprobante);
    try {
      const html = await comprobantesPrintData({ id_comprobantes: id_comprobante });
      setComprobanteHtml(html);
    } catch (error) {
      console.error("Error al obtener el comprobante:", error);
    } finally {
      setComprobanteLoading(false);
    }
  };

  if (!data.data.length) {
    return (
      <Grid style={{ textAlign: "center", padding: "40px 0" }}>
        <Typography variant="h6" style={{ color: "#3c4b64" }}>
          Aun no posee un historico.
        </Typography>
      </Grid>
    );
  }

  return (
    <>
    <ComprobanteModal open={!!comprobanteHtml} html={comprobanteHtml || ""} onClose={() => setComprobanteHtml(null)} />
    <TimelineWrapper>
      {tipoHistorico &&
        data.data.map((h, index) => {
          const ubicacion = typeof h.ubicacion === "object" ? "" : h.ubicacion?.trim();
          const usuario   = typeof h?.usuario === "object" ? "" : h?.usuario;
          const prioridad = typeof h?.comprobante_detalle_emision === "object" ? "" : h?.comprobante_detalle_emision;
          const nombre    = typeof h?.comprobante_nombre === "object" ? "" : h?.comprobante_nombre;
          const hasComp   =
            h?.comprobante_id &&
            !(typeof h.comprobante_id === "object" && Object.keys(h.comprobante_id).length === 0);
          const config  = getConfig(h.tipo_movimiento);
          return (
            <TimelineRow
              key={index}
              tipo_movimiento={h.tipo_movimiento}
              fecha={h.fecha}
              fields={[
                { icon: LocationOnIcon, label: "Ubicación", value: ubicacion },
                { icon: PersonIcon,     label: "Usuario",   value: usuario },
                { icon: null,           label: "Prioridad", value: prioridad },
                { icon: null,           label: "Nombre",    value: nombre },
              ]}
              extraData={h.extra_data}
              useLabel
              printBtn={
                hasComp ? (
                  <Box sx={{ mt: 0.75 }}>
                    <Button
                      onClick={() => handlePrint(h.comprobante_id)}
                      variant="outlined"
                      size="small"
                      startIcon={comprobanteLoading === h.comprobante_id ? <CircularProgress size={12} /> : <PrintIcon />}
                      disabled={comprobanteLoading === h.comprobante_id}
                      sx={{
                        borderColor: config.color,
                        color: config.color,
                        fontSize: "0.8rem",
                        py: 0.4,
                        "&:hover": { bgcolor: `${config.color}12` },
                      }}
                    >
                      Comprobante
                    </Button>
                  </Box>
                ) : null
              }
            />
          );
        })}

      {!tipoHistorico && (
        <>
          {valorContenido && typeof valorContenido !== "object" && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              {valorContenido}
            </Typography>
          )}
          {data.data.map((c, index) => {
            const detalle   = typeof c.detalle === "object" ? "" : c.detalle;
            const prioridad = typeof c?.comprobante_detalle_emision === "object" ? "" : c?.comprobante_detalle_emision;
            const nombre    = typeof c?.comprobante_nombre === "object" ? "" : c?.comprobante_nombre;
            const showExtra = c.tipo_movimiento?.includes("Devolución") || c.tipo_movimiento?.includes("Retiro");

            return (
              <TimelineRow
                key={index}
                tipo_movimiento={c.tipo_movimiento}
                fecha={c.fecha}
                  fields={[
                  { icon: null, label: "Detalle",   value: detalle },
                  { icon: null, label: "Prioridad", value: prioridad },
                  { icon: null, label: "Nombre",    value: nombre },
                ]}
                extraData={showExtra ? c.extra_data : null}
              />
            );
          })}
        </>
      )}
    </TimelineWrapper>
    </>
  );
};
